import AxiosService from "plugins/TS-lib-utils-public/services/AxiosService";

/**
 * @class PatientService
 * @extends AxiosService
 * @description Service layer for the patients management API (`aspect-users`).
 * Covers paginated list retrieval, on-demand detail fetching with server-side
 * sub-document pagination and filtering, and profile updates.
 */
class PatientService extends AxiosService {
  constructor() {
    super("aspect-users");
  }

  /**
   * Retrieves a paginated list of patient users.
   * Response shape: `{ data: { total: number, data: Patient[] } }`
   * Each Patient contains `total_practices` (number) but NOT `practices` array.
   *
   * @param {number} page - Zero-based page index.
   * @param {number} limit - Number of records per page.
   * @param {string|null} [search=null] - Optional full-text search term.
   * @returns {Promise<import("axios").AxiosResponse>}
   */
  getUserPatient = async (page, limit, search = null) => {
    const base = `/patients/list?page=${page}&limit=${limit}`;
    const query = search
      ? `${base}&search=${encodeURIComponent(search)}`
      : base;

    return await this.get(query, {
      headers: { Authorization: `Bearer ${this.getToken()}` },
    });
  };

  /**
   * Retrieves full details for a single patient, including paginated
   * `practices` and their nested doctor sub-documents with server-side filtering.
   *
   * @param {string} userId - The `auth_user_id` of the target patient.
   * @param {number} [detailsPage=0] - Zero-based practice page index.
   * @param {number} [detailsLimit=50] - Max practices to return per call.
   * @param {number} [subDetailsPage=0] - Zero-based doctor page index within each practice.
   * @param {number} [subDetailsLimit=10] - Max doctors per practice per call.
   * @param {string} [searchQuery=''] - Optional search term to filter sub-documents on the server.
   * @returns {Promise<import("axios").AxiosResponse>}
   */
  getPatientDetails = async (
    userId,
    detailsPage = 0,
    detailsLimit = 50,
    subDetailsPage = 0,
    subDetailsLimit = 10,
    searchQuery = ''
  ) => {
    let query = [
      `/patients/list`,
      `?page=0&limit=1`,
      `&target_user_id=${userId}`,
      `&details_page=${detailsPage}`,
      `&details_limit=${detailsLimit}`,
      `&sub_details_page=${subDetailsPage}`,
      `&sub_details_limit=${subDetailsLimit}`,
    ].join("");

    if (searchQuery && searchQuery.trim() !== '') {
      query += `&search=${encodeURIComponent(searchQuery.trim())}`;
    }

    return await this.get(query, {
      headers: { Authorization: `Bearer ${this.getToken()}` },
    });
  };

  /**
   * Submits a management-level profile update for a patient.
   *
   * @param {Object} data - Profile fields to update. Must include `auth_user_id`.
   * @returns {Promise<import("axios").AxiosResponse>}
   */
  managementUserEdit = async (data) => {
    return await this.put(`/patients/${data.auth_user_id}`, data, {
      headers: { Authorization: `Bearer ${this.getToken()}` },
    });
  };

  /**
   * Submits an external reference update for a patient's associated practices.
   * Bypasses the default base URL to target the correct microservice.
   *
   * @param {Object} data - Payload containing set_ext_refs and auth_user_id.
   * @returns {Promise<import("axios").AxiosResponse>}
   */
  updateExtRef = async (data) => {
    const targetUrl = this.baseUrl.replace("aspect-users", "aspect-patient-user") + `/profile/${data.auth_user_id}/ext_ref`;
    
    return await this.axios.put(targetUrl, data, {
      headers: { Authorization: `Bearer ${this.getToken()}` },
    });
  };
}

export default new PatientService();